import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { WalletService } from "../services/walletsService";
import type { OrganizationWalletBalance } from "../types/wallets-api.types";

// Shared by the wallet banner, create-card modal, and load-pocket cap — used
// to fire 3-4 identical requests on mount. Singleton dedupes via the
// in-flight map; `invalidateOrganizationWalletBalance` forces a refetch
// after operations that move the wallet balance (topup, withdrawal, transfer).
const STALE_AFTER_MS = 30_000;

type CacheEntry = {
  balance: OrganizationWalletBalance;
  fetchedAt: number;
};

type InFlight = Promise<OrganizationWalletBalance>;

const entries = new Map<string, CacheEntry>();
const inflight = new Map<string, InFlight>();
const listeners = new Map<string, Set<() => void>>();

function notify(orgId: string): void {
  const set = listeners.get(orgId);
  if (!set) return;
  for (const fn of set) fn();
}

async function loadFromServer(orgId: string): Promise<OrganizationWalletBalance> {
  try {
    const response = await WalletService.getOrganizationCardWalletBalance(orgId);
    return response.data.balance;
  } catch {
    // One automatic retry: the backend provisions the organization wallet on
    // demand when it is missing (e.g. after switching provider), so a transient
    // first failure usually recovers on the next read.
    const response = await WalletService.getOrganizationCardWalletBalance(orgId);
    return response.data.balance;
  }
}

async function ensureFresh(orgId: string): Promise<OrganizationWalletBalance> {
  const existing = entries.get(orgId);
  if (existing && Date.now() - existing.fetchedAt < STALE_AFTER_MS) {
    return existing.balance;
  }

  let promise = inflight.get(orgId);
  if (!promise) {
    promise = loadFromServer(orgId)
      .then((balance) => {
        entries.set(orgId, { balance, fetchedAt: Date.now() });
        return balance;
      })
      .finally(() => {
        inflight.delete(orgId);
      });
    inflight.set(orgId, promise);
  }

  const balance = await promise;
  notify(orgId);
  return balance;
}

export function invalidateOrganizationWalletBalance(orgId: string): void {
  entries.delete(orgId);
  notify(orgId);
}

export function useOrganizationWalletBalance(): {
  balance: OrganizationWalletBalance | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const { user } = useAuth();
  const orgId = user?.organization_id ?? user?.organization?.id ?? null;

  const [balance, setBalance] = useState<OrganizationWalletBalance | null>(() =>
    orgId ? (entries.get(orgId)?.balance ?? null) : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      // Reset on logout / org switch so the previous org's balance does not
      // leak into the new session.
      /* eslint-disable react-hooks-eslint/set-state-in-effect */
      setBalance(null);
      setIsLoading(false);
      /* eslint-enable react-hooks-eslint/set-state-in-effect */
      return;
    }

    let cancelled = false;

    const subscribe = () => {
      const current = entries.get(orgId)?.balance ?? null;
      if (!cancelled) setBalance(current);
    };

    const set = listeners.get(orgId) ?? new Set();
    set.add(subscribe);
    listeners.set(orgId, set);

    setIsLoading(true);
    setError(null);
    ensureFresh(orgId)
      .then((next) => {
        if (cancelled) return;
        setBalance(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(HttpApiService.getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      const next = listeners.get(orgId);
      if (next) {
        next.delete(subscribe);
        if (next.size === 0) listeners.delete(orgId);
      }
    };
  }, [orgId]);

  const refresh = async (): Promise<void> => {
    if (!orgId) return;
    invalidateOrganizationWalletBalance(orgId);
    try {
      const fresh = await ensureFresh(orgId);
      setBalance(fresh);
    } catch (err: unknown) {
      setError(HttpApiService.getErrorMessage(err));
    }
  };

  return { balance, isLoading, error, refresh };
}
